import { groupsJson, operatorsJson } from "@corca/data";
import { latexExportsJson } from "@corca/data/latex-exports";
import { chunk } from "es-toolkit";

import { jsonTemplater } from "./jsonTemplater";
import { sanitizeLatex } from "./sanitizeLatex";
import { sanitizeNonAscii } from "./sanitizeNonAscii";

import type { NodeJson } from "@corca/data";

const ATTACHMENT_TYPES = ["label", "decorator"];

const groupOpeningSymbolMap = Object.fromEntries(
  Object.entries(groupsJson).map(([_key, group]) => [
    group.openingSymbol.symbol,
    group.openingSymbol,
  ]),
);

const groupClosingSymbolMap = Object.fromEntries(
  Object.entries(groupsJson).map(([_key, group]) => [
    group.closingSymbol.symbol,
    group.closingSymbol,
  ]),
);

function getGroupSymbolText(
  symbol: string | undefined,
  symbolMap: Record<string, { symbol: string; key: string }>,
  prefix = "",
) {
  if (!symbol) return `${prefix}.`;
  const groupSymbol = symbolMap[symbol];
  if (!groupSymbol) return `${prefix}.`;
  return latexExportsJson[groupSymbol.key]?.exports || `${prefix}${groupSymbol.symbol}`;
}

function getMatrixText(matrixNode: NodeJson, cols = 1): LatexVisitorResult {
  const matrix = chunk(
    matrixNode.children.filter((n) => n.type === "inline"),
    cols,
  );

  return mergeResults(
    matrix.map((row) => mergeResults(row.map(nodeToLatex), " & ")),
    " \\\\ ",
  );
}

function getDecoratorsText(
  str: string,
  node: NodeJson,
  decoratorCallback?: (decoratorTemplate: LatexVisitorResult) => void,
): string {
  const decoratorNodes = node.children.filter((n) => n.type === "decorator");

  if (decoratorNodes.length === 0) return str;

  return decoratorNodes.reduce((acc, decorator) => {
    const decoratorTemplate = nodeToLatex(decorator);
    decoratorCallback?.(decoratorTemplate);
    return decoratorTemplate ? jsonTemplater(decoratorTemplate.text, { parameters: [acc] }) : acc;
  }, str);
}

type LatexVisitorResult = { text: string; packages: Set<string> };

function mergeResults(
  results: LatexVisitorResult[],
  separator: string,
  filter?: (str: string) => boolean,
): LatexVisitorResult {
  const packages = new Set<string>();
  const texts = results.map((r) => {
    r.packages.forEach((p) => packages.add(p));
    return r.text;
  });
  const filteredText = filter ? texts.filter(filter) : texts;
  return { text: filteredText.join(separator), packages };
}

/**
 * Render node in the LaTeX form.
 * This is a rewrite of apps/web/src/app/models/nodes/visitors/LatexVisitor.ts
 */
export function nodeToLatex(node: NodeJson): LatexVisitorResult {
  // TODO: export dependencies
  switch (node.type) {
    case "wrapper": {
      return mergeResults(node.children.map(nodeToLatex), "");
    }

    case "inline": {
      const packages = new Set<string>();
      const inlineNodes = node.children.filter((n) => !ATTACHMENT_TYPES.includes(n.type));

      const arr: string[] = [];

      for (const child of inlineNodes) {
        const { text: childStr, packages: childPackages } = nodeToLatex(child);
        childPackages.forEach((p) => packages.add(p));

        if (childStr.length > 0) {
          if (childStr[0] === "^" || childStr[0] === "_") {
            const lastIdx = arr.length - 1;

            // prevent double subscript or superscript by wrapping previous child
            // x + y^2^2 => x + { y^2 }^2
            if (arr[lastIdx]?.includes(childStr[0])) {
              arr[lastIdx] = `{ ${arr[lastIdx]} }`;
            }
          } else if (arr.length > 0) {
            arr.push(" ");
          }

          arr.push(childStr);
        }
      }

      return { text: arr.join(""), packages };
    }

    case "symbol": {
      const packages = new Set<string>();
      const labelNodes = node.children.filter((n) => n.type === "label");

      const topRightLabels = mergeResults(
        labelNodes.filter((n) => n.props?.position === "right-top").map(nodeToLatex),
        " ",
        (s) => s.length > 0,
      );
      topRightLabels.packages.forEach((p) => packages.add(p));

      const bottomRightLabels = mergeResults(
        labelNodes.filter((n) => n.props?.position === "right-bottom").map(nodeToLatex),
        " ",
        (s) => s.length > 0,
      );
      bottomRightLabels.packages.forEach((p) => packages.add(p));

      const template = node.props.key && (latexExportsJson[node.props.key]?.exports as string);
      let str = (template as string) ?? node.props.value;

      str = getDecoratorsText(str, node, (decoratorTemplate) => {
        decoratorTemplate.packages.forEach((p) => packages.add(p));
      });

      if (topRightLabels.text.length > 0) {
        str += `^{${topRightLabels.text}}`;
      }

      if (bottomRightLabels.text.length > 0) {
        str += `_{${bottomRightLabels.text}}`;
      }

      str = sanitizeNonAscii(str);

      return { text: str, packages };
    }

    case "variable": {
      const packages = new Set<string>();
      const symbolNode = node.children.find((child) => child.type === "symbol");
      if (!symbolNode) {
        return { text: "", packages };
      }

      const parameters = node.children.filter((child) => child.type === "inline");
      const hasParams = parameters.length > 0;
      const symbolResult = nodeToLatex(symbolNode);

      symbolResult?.packages.forEach((p) => packages.add(p));

      const powerNode = node.children.find(
        (child) => child.type === "operator" && child.props.operatorType === "power",
      );
      const powerResult = powerNode ? nodeToLatex(powerNode) : null;
      if (powerResult) {
        powerResult.packages.forEach((p) => packages.add(p));
      }

      const powerText = powerResult?.text ?? "";

      if (hasParams) {
        const args = mergeResults(parameters.map(nodeToLatex), ",");
        args.packages.forEach((p) => packages.add(p));
        const left = getGroupSymbolText(
          node.props.openingSymbol as string,
          groupOpeningSymbolMap,
          "\\left",
        );
        const right = getGroupSymbolText(
          node.props.closingSymbol as string,
          groupClosingSymbolMap,
          "\\right",
        );
        return { text: `${symbolResult.text}${powerText} ${left} ${args.text} ${right}`, packages };
      }

      return { text: symbolResult.text, packages };
    }

    case "operatorSymbol":
      return mergeResults(node.children.map(nodeToLatex), "");

    case "operator": {
      const packages = new Set<string>();
      const template = node.props.key && latexExportsJson[node.props.key]?.exports;

      const parameters = node.children.filter((child) => child.type === "inline");
      const paramLatex = mergeResults(parameters.map(nodeToLatex), ",");
      paramLatex.packages.forEach((p) => packages.add(p));

      const operatorSymbol =
        (node.props.name as string) ||
        (node.props?.operatorType && operatorsJson[node.props.operatorType]?.symbol) ||
        "?";

      if (!template) {
        return { text: `\\text{${operatorSymbol}}(${paramLatex.text})`, packages };
      }

      return {
        text: jsonTemplater(template, {
          operatorType: node.props.operatorType,
          operatorSymbol,
          parameters: parameters.map(nodeToLatex).map((p) => p.text),
        }),
        packages,
      };
    }

    case "group": {
      const packages = new Set<string>();
      const matrixNode = node.children[0];
      if (matrixNode?.type !== "matrix") return { text: "", packages };
      const { rows, cols } = matrixNode.props;

      if (node.props.casesSystemEnabled) {
        const body = getMatrixText(matrixNode, cols);
        body.packages.forEach((p) => packages.add(p));

        let text = `\\begin{cases} ${body.text} \\end{cases}`;
        text = getDecoratorsText(text, node, (decoratorTemplate) => {
          decoratorTemplate.packages.forEach((p) => packages.add(p));
        });

        return { text, packages };
      }

      const body =
        rows === 1 && cols === 1
          ? mergeResults(matrixNode.children.map(nodeToLatex), "")
          : mergeResults(node.children.map(nodeToLatex), "");
      body.packages.forEach((p) => packages.add(p));

      const left = getGroupSymbolText(
        node.props.openingSymbol as string,
        groupOpeningSymbolMap,
        "\\left",
      );
      const right = getGroupSymbolText(
        node.props.closingSymbol as string,
        groupClosingSymbolMap,
        "\\right",
      );

      let text = `${left} ${body.text} ${right}`;

      text = getDecoratorsText(text, node, (decoratorTemplate) => {
        decoratorTemplate.packages.forEach((p) => packages.add(p));
      });

      return { text, packages };
    }

    case "function": {
      const packages = new Set<string>();
      const symbolNode = node.children.find((child) => child.type === "symbol");
      const parameters = node.children.filter((child) => child.type === "inline");
      if (!symbolNode) {
        return { text: "", packages };
      }
      const symbolResult = nodeToLatex(symbolNode);
      symbolResult.packages.forEach((p) => packages.add(p));

      const powerNode = node.children.find(
        (child) => child.type === "operator" && child.props.operatorType === "power",
      );
      const powerResult = powerNode ? nodeToLatex(powerNode) : null;
      if (powerResult) {
        powerResult.packages.forEach((p) => packages.add(p));
      }
      const powerText = powerResult?.text ?? "";

      const template = node.props.key && latexExportsJson[node.props.key]?.exports;

      const { parenthesesVisibility = "auto" } = node.props;
      let omitParentheses = parenthesesVisibility === "hidden";

      if (parenthesesVisibility === "auto" && parameters.length === 1) {
        const paramChildren = parameters[0].children;
        omitParentheses = paramChildren.length <= 1 && paramChildren[0]?.type !== "group";
      }

      const left = omitParentheses
        ? ""
        : getGroupSymbolText(
            (node.props.openingSymbol as string) ?? "(",
            groupOpeningSymbolMap,
            "\\left",
          );
      const right = omitParentheses
        ? ""
        : getGroupSymbolText(
            (node.props.closingSymbol as string) ?? ")",
            groupClosingSymbolMap,
            "\\right",
          );

      const paramLatex = parameters.map(nodeToLatex);
      const paramResult = mergeResults(paramLatex, ",");
      paramResult.packages.forEach((p) => packages.add(p));

      if (!template) {
        const renderedParams = `${left}${paramResult.text}${right}`;

        return {
          text: symbolResult.text.startsWith("\\")
            ? `${symbolResult.text}${powerText}${renderedParams}`
            : `\\text{${symbolResult.text}}${powerText}${renderedParams}`,
          packages,
        };
      }

      return {
        text: jsonTemplater(template, {
          value: symbolNode?.props.value,
          symbol: symbolResult.text,
          power: powerText,
          parameters: paramLatex.map((p) => p.text),
          openingSymbol: left,
          closingSymbol: right,
        }),
        packages,
      };
    }

    case "plainText": {
      const { value } = node.props;
      return { text: `\\text{${sanitizeLatex(value)}}`, packages: new Set() };
    }

    case "number": {
      const packages = new Set<string>();
      const body = mergeResults(node.children.map(nodeToLatex), "");
      body.packages.forEach((p) => packages.add(p));
      return { text: `${node.props.value}${body.text}`, packages };
    }

    case "constant": {
      const mainChild = node.children.find((child) => !ATTACHMENT_TYPES.includes(child.type));
      return mainChild ? nodeToLatex(mainChild) : { text: "", packages: new Set() };
    }

    case "decorator": {
      const template = node.props.key && latexExportsJson[node.props.key]?.exports;
      return { text: template || "", packages: new Set() };
    }

    case "label": {
      // Assume only one child node: the labeled node
      return mergeResults(node.children.map(nodeToLatex), "");
    }

    case "line": {
      return mergeResults(node.children.map(nodeToLatex), "");
    }

    case "text": {
      return mergeResults(node.children.map(nodeToLatex), "\n\n");
    }

    case "unit": {
      return mergeResults(node.children.map(nodeToLatex), "");
    }

    case "matrix": {
      const matrixResult = getMatrixText(node, node.props.cols);

      const { augmentedBarIndices = [] } = node.props;

      if (augmentedBarIndices.length > 0) {
        const colsText = Array.from({ length: node.props.cols })
          .map((_, colIndex) => `c${augmentedBarIndices.includes(colIndex) ? "|" : ""}`)
          .join("");

        return {
          text: `\\begin{array}{${colsText}} ${matrixResult.text} \\end{array}`,
          packages: matrixResult.packages,
        };
      }

      return {
        text: `\\begin{matrix} ${matrixResult.text} \\end{matrix}`,
        packages: matrixResult.packages,
      };
    }

    case "graph":
      return { text: "", packages: new Set() };

    case "graphExpression":
      return { text: "", packages: new Set() };

    case "placeholder":
      return { text: "", packages: new Set() };

    case "computedReference": {
      const value = mergeResults(node.children.map(nodeToLatex), "");
      if (!node.props.bracketed) return value;

      const left = getGroupSymbolText("(", groupOpeningSymbolMap, "\\left");
      const right = getGroupSymbolText(")", groupClosingSymbolMap, "\\right");
      return { ...value, text: `${left} ${value.text} ${right}` };
    }

    case "computedResult": {
      return mergeResults(node.children.map(nodeToLatex), "");
    }
  }
}
